import React from 'react';
import {
  Image,
  StyleSheet,
  View,
  ScrollView,
  Text,
  TouchableHighlight,
} from './components/base_components';
import { BusyComponent, BusyOverlay } from './components/busy_component';
import Link from './components/link';
import ProgressSpinner from './components/progress_spinner';
import MediaIcon from './components/media_icon';
import TextButton from './components/buttons/text_button';

import DeviceStore from './stores/device_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

const LOGO_IMG = require('./assets/img/logo.png');

const styles = StyleSheet.create({
  deviceVolumeScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  scroller: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  scrollInner: {
    paddingLeft: '3em',
    paddingRight: '3em',
    paddingTop: '1em',
    paddingBottom: '5em',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflowY: 'scroll',
  },
  top: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fill: {
    flex: 1,
  },
  logo: {
    width: '8em',
    height: '8em',
    marginRight: '1em',
  },
  line: {
    marginBottom: '0.5em',
    alignSelf: 'stretch',
    height: '1px',
    backgroundColor: 'black',
  },
  headerText: {
    color: '#444',
    fontSize: '1.4em',
    fontWeight: 'bold',
  },
  list: {
    marginTop: '1em',
    marginBottom: '1em',
  },
  add: {
    marginTop: '1em',
  },
  mediaList: {
    alignSelf: 'stretch',
    marginTop: '0.5em',
    marginBottom: '1em',
    flexDirection: 'column',
  },
  dirEntry: {
    paddingLeft: '0.2em',
    paddingRight: '0.2em',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    ':hover': {
      backgroundColor: 'lightblue !important',
    },
  },
  mediaHighlight: {
    padding: '0.2em',
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    margin: '0.2em',
    padding: '0.3em',
    marginRight: '0.8em',
    width: '1.5em',
    height: '1.5em',
    backgroundColor: '#aaa',
    borderRadius: '0.3em',
  },
  mediaText: {
    marginBottom: '0.2em',
    width: '50em',
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mediaSize: {
    marginLeft: '5em',
    width: '10em',
  },
  mediaStatus: {
    marginLeft: '2em',
  },
  title: {
    marginBottom: '0.2em',
    fontSize: '1.5em',
    fontWeight: 'bold',
  },
  odd: {
    backgroundColor: '#f1f1f1',
  },
  topText: {
    marginTop: '0.3em',
    fontSize: '1.2em',
  },
  topLine: {
    marginTop: '1em',
    marginBottom: '1em',
  },
  button: {
    marginTop: '1em',
    height: '2.5em',
  },
  summary: {
    marginBottom: '0.3em',
  },
  summaryLabel: {
    marginRight: '0.2em',
  },
  summaryValue: {
    marginRight: '1em',
    fontWeight: 'bold',
  },
});

type Props = {};
type State = {
  is_admin: boolean;
  device_id: number;
  volume_id: number;
  device: any;
  upload_list: any;
  download_list: any;
};

export default class DeviceVolumeScreen extends BusyComponent<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);

    const device_id = parseInt(props.match.params.device_id) || 0;
    const volume_id = parseInt(props.match.params.volume_id) || 0;
    this.state = {
      is_admin: UserStore.getUser().is_admin,
      device_id,
      volume_id,
      device: null,
      upload_list: null,
      download_list: null,
    };
  }
  componentDidMount() {
    const { device_id, volume_id } = this.state;
    DeviceStore.addChangeListener(this._onUpdate);
    DeviceStore.fetchDevice(device_id);
    DeviceStore.fetchVolume({ device_id, volume_id });
    this._onUpdate();
  }
  componentWillUnmount() {
    DeviceStore.removeChangeListener(this._onUpdate);
  }
  _onUpdate = () => {
    const { device_id, volume_id } = this.state;
    const info = DeviceStore.getVolume(device_id, volume_id);
    const upload_list = (info && info.upload_list) || null;
    const download_list = (info && info.download_list) || null;
    const device = DeviceStore.getDevice(device_id);

    this.setState({ device, upload_list, download_list });
  };

  _onUploadCalc = () => {
    const { volume_id } = this.state;
    if (this.setBusy()) {
      const opts = { volume_id };
      VolumeStore.calcVolumeUploads(opts, (err) => {
        this.clearBusy();
        if (err) {
          window.alert('Probably is just taking a long time, go get a coffee.');
        }
      });
    }
  };
  _onDownloadCalc = () => {
    const { volume_id } = this.state;
    if (this.setBusy()) {
      const opts = { volume_id };
      VolumeStore.calcVolumeDownloads(opts, (err) => {
        this.clearBusy();
        if (err) {
          window.alert('Probably is just taking a long time, go get a coffee.');
        }
      });
    }
  };
  _onMediaPress = (media) => {
    const { volume_id } = this.state;
    const { media_path } = media;
    const dir = media_path
      .split('/')
      .slice(0, -1)
      .join('/');
    this.props.history.push(`/volume/${volume_id}${dir}`);
  };

  render() {
    const {
      is_admin,
      device,
      download_list,
      upload_list,
      volume_id,
    } = this.state;

    let downloads = null;
    if (!download_list) {
      downloads = <ProgressSpinner />;
    } else if (download_list.length === 0) {
      downloads = <Text style={styles.noMedia}>All Synced!</Text>;
    } else {
      downloads = download_list.map((entry, i) => (
        <DirEntry
          key={i}
          odd={i % 2}
          entry={entry}
          onPress={this._onMediaPress}
        />
      ));
    }
    let uploads = null;
    if (!upload_list) {
      uploads = <ProgressSpinner />;
    } else if (upload_list.length === 0) {
      uploads = <Text style={styles.noMedia}>All Synced!</Text>;
    } else {
      uploads = upload_list.map((entry, i) => (
        <DirEntry
          key={i}
          odd={i % 2}
          entry={entry}
          onPress={this._onMediaPress}
        />
      ));
    }

    let top = null;
    if (device) {
      const volume = device.volume_list.find((v) => v.volume_id === volume_id);

      let admin_calc = null;
      if (is_admin) {
        admin_calc = (
          <>
            <TextButton
              style={styles.button}
              text='Recalc Uploads'
              onPress={this._onUploadCalc}
            />
            <TextButton
              style={styles.button}
              text='Recalc Downloads'
              onPress={this._onDownloadCalc}
            />
          </>
        );
      }

      top = (
        <>
          <Text style={styles.topText}>
            Device: {device && device.device_name}
          </Text>
          <Text style={styles.topText}>User: {device && device.user_name}</Text>
          <Line style={styles.topLine} />
          <Text style={styles.topText}>
            Volume: {volume && volume.volume_name}
          </Text>
          {admin_calc}
        </>
      );
    } else {
      top = <ProgressSpinner />;
    }

    return (
      <View style={styles.deviceVolumeScreen}>
        <ScrollView
          style={styles.scroller}
          contentContainerStyle={styles.scrollInner}
        >
          <View style={styles.top}>
            <Image style={styles.logo} source={LOGO_IMG} />
            <Text style={styles.headerText}>MONP ABT</Text>
            <View style={styles.fill} />
            <Link href='/logout' text='Logout' />
          </View>
          <Line />
          {top}
          <Line style={styles.topLine} />
          <Text style={styles.title}>Pending Uploads:</Text>
          <MediaSummary list={upload_list} />
          <View style={styles.mediaList}>{uploads}</View>
          <Text style={styles.title}>Pending Downloads:</Text>
          <MediaSummary list={download_list} />
          <View style={styles.mediaList}>{downloads}</View>
        </ScrollView>
        <BusyOverlay isBusy={this.state.is_busy} />
      </View>
    );
  }
}

function Line(props) {
  return <View style={[styles.line, props.style]} />;
}
function MediaSummary(props) {
  const { list } = props;

  let content = null;
  if (list) {
    const mb = _bytes(list.reduce((memo, l) => l.file_size + memo, 0)) || '0';

    content = (
      <View style={styles.summary}>
        <Text style={styles.summaryLabel}>Count:</Text>
        <Text style={styles.summaryValue}>{list.length}</Text>
        <Text style={styles.summaryLabel}>Bytes:</Text>
        <Text style={styles.summaryValue}>{mb}</Text>
      </View>
    );
  }
  return content;
}
function DirEntry(props) {
  const { entry, onPress, odd } = props;
  const {
    media_id,
    media_path,
    file_size,
    in_s3,
    in_filerunner,
    in_filestore,
  } = entry;

  let status = 'Not uploaded';
  if (in_s3) {
    status = 'In S3';
  } else if (in_filerunner) {
    status = 'In Filerunner';
  } else if (in_filestore) {
    status = 'In Filestore';
  }

  const _onPress = () => {
    onPress(entry);
  };
  return (
    <View style={[styles.dirEntry, odd ? styles.odd : null]}>
      <MediaIcon style={styles.icon} name={media_path} />
      <TouchableHighlight
        style={styles.mediaHighlight}
        underlayColor='rgba(0,0,0,0.1)'
        onPress={_onPress}
      >
        <Text style={styles.mediaText} title={media_path}>
          {media_path}
        </Text>
      </TouchableHighlight>
      <Text style={styles.mediaSize}>{_bytes(file_size)}</Text>
      <Text style={styles.mediaStatus}>{status}</Text>
    </View>
  );
}

function _bytes(number) {
  let ret = '';
  if (number) {
    ret = (number / 1024 / 1024).toFixed(3) + 'MB';
  }
  return ret;
}
